import React, { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { format } from 'date-fns'
import { useTheme } from 'styled-components'
import { formatPercentage, useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Skeleton, Typography } from '@chilecompra/react-kit/components'
import {
  PUBLIC_URL,
  REACT_APP_OPENED_DATA_URL,
  REACT_APP_SEARCH_PURCHSE_ORDER
} from '../../config/settings/environment'
import { onGetTotalPurchaseOrdersThunk } from './PurchaseOrdersSection.actions'
import PieChart from '../../components/Charts/PieChart'
import {
  ArrowForwardIcon,
  Card,
  CardItem,
  Column,
  ColumnChart,
  NotFoundImg,
  RedirectLink,
  Root,
  Row,
  RowChart,
  Strong,
  Separator,
  WarningImg
} from './PurchaseOrdersSection.styles'
import { formatAmount } from '../../modules/utils/formatters'

/**
 * The Purchase Order section container.
 */
const PurchaseOrdersSection = () => {
  const dni = usePathParam('rut')
  const theme = useTheme()
  const dispatch = useDispatch()
  const {
    size: { isTiny, isUpToMedium }
  } = useViewport()

  const { error, loading, hasData, purchaseOrders, rangeDate, totalOrder } = useSelector(
    state => state.totalPurchaseOrders
  )

  const { entCode } = useSelector(state => state.basicBuyerInformation)
  const handlePieChartTooltipFormatter = useCallback(function () {
    return `
        <p>
          <strong>${formatPercentage(this.point.percentage)}  </strong> Órdenes de compras generadas en <strong>${this.key}</strong>
        </p>
      `
  }, [])

  const generateSearchPurchaseOrderUrl = useMemo(() => {
    const today = new Date()
    const oneMonthAgo = new Date()

    oneMonthAgo.setMonth(today.getMonth() - 1)

    const dateFrom = format(oneMonthAgo, 'yyyy-MM-dd')
    const dateTo = format(today, 'yyyy-MM-dd')

    return `${REACT_APP_SEARCH_PURCHSE_ORDER}?buyer_code=${entCode}&date_from=${dateFrom}&date_to=${dateTo}`
  }, [entCode])

  useEffectOnce(() => {
    dispatch(onGetTotalPurchaseOrdersThunk(dni))
  }, [dni, dispatch])

  return (
    <Root>
      {!loading && (
        <>
          <Row>
            <Column maxWidth="595px" padding="10px 10px 0 0">
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Resumen de sus órdenes de compra
              </Typography>
              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                <Strong>Fuente:</Strong> Órdenes de compra del organismo público en Mercado Público.
              </Typography>
            </Column>
          </Row>
          {hasData && (
            <Row>
              <Column maxWidth="500px">
                <Typography fontSize="16px" fontWeight="regular" variant="body1" lineHeight="21px" tabIndex="0">
                  Cifras generales de las órdenes de compra generadas por la institución durante el periodo de{' '}
                  <strong>
                    {rangeDate?.initialtMonth} {rangeDate?.initialYear} a {rangeDate?.finalMonth} {rangeDate?.finalYear}
                    .
                  </strong>
                </Typography>
              </Column>
            </Row>
          )}
        </>
      )}
      {!error && !loading && !hasData && (
        <Card enableBorder margin="24px 0 50px 0" display="flex" color="blank" textAlign="center">
          <CardItem flexDirection="column">
            <NotFoundImg
              alt="No se encontraron resultados"
              margin="48px 0 0 0"
              src={`${PUBLIC_URL}/assets/images/notFound.svg`}
            />
            <Typography variant="h4" fontWeight="bold" margin="14px 0 8px 0" tabIndex="0">
              No existe información
            </Typography>
            <Row justifyContent="center" textAlign="center" width={isTiny ? '70%' : '40%'}>
              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                El organismo comprador no ha emitido órdenes de compra durante los últimos 12 meses.
              </Typography>
            </Row>
          </CardItem>
        </Card>
      )}
      {error && !loading && (
        <Card
          display="flex"
          enableBorder
          color="blank"
          margin="43px 0 44px 0"
          alignItems="center"
          height={isTiny ? '139px' : '243px'}
        >
          <CardItem direction="row" padding="25px 16px">
            <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
            <Typography
              color="black1"
              variant="body1"
              fontWeight="regular"
              fontSize="16px"
              lineHeight="21px"
              margin="0 0 0 33px"
              tabIndex="0"
            >
              No ha sido posible obtener información de la institución.
              <br />
              Por favor, te pedimos que lo vuelvas a intentar dentro de unos minutos.
            </Typography>
          </CardItem>
        </Card>
      )}
      {!error && !loading && hasData && (
        <RowChart justifyContent="space-around" margin="24px 0 32px 0">
          <>
            <ColumnChart
              lg={6}
              sm={7}
              xs={12}
              alignContent="center"
              flexWrap="wrap"
              justifyContent="center"
              padding="2px 2px"
            >
              <PieChart
                data={purchaseOrders.map(e => {
                  return {
                    ...e,
                    y: e.totalOC,
                    color: theme.palette.brand[e.colorName]
                  }
                })}
                minSize={isTiny ? '30' : '40'}
                size="50%"
                spacing={isTiny ? [-20, 0, -10, 0] : [0, 0, 0, 5]}
                hoverColor={theme.palette.brand.primary}
                id="purchase-orders-pie-chart"
                tooltipFormatter={handlePieChartTooltipFormatter}
              />
            </ColumnChart>
            <ColumnChart flexDirection="column" justifyContent="center" lg={5} sm={5} xs={12}>
              <Row justifyContent="space-around">
                <ColumnChart lg={2} sm={4} xs={4} justifyContent="center">
                  <Typography
                    color="black1"
                    variant="h3"
                    fontWeight="bold"
                    fontSize="22px"
                    lineHeight="26px"
                    tabIndex="0"
                  >
                    {formatAmount(totalOrder)}
                  </Typography>
                </ColumnChart>
                <ColumnChart lg={10} sm={8} xs={8} padding="3px 28px 0 0">
                  <Typography
                    color="black1"
                    variant="body2"
                    fontWeight="regular"
                    fontSize="14px"
                    lineHeight="18px"
                    tabIndex="0"
                  >
                    Órdenes de compra generadas
                  </Typography>
                </ColumnChart>
              </Row>
              <Separator />

              {purchaseOrders.map(item => (
                <Row key={item.name} alignItems="center" justifyContent="space-around">
                  <ColumnChart lg={2} sm={4} xs={4} justifyContent="center">
                    <Typography
                      color="black1"
                      variant="h4"
                      fontWeight="bold"
                      fontSize="18px"
                      lineHeight="22px"
                      tabIndex="0"
                    >
                      {formatAmount(item.totalOC)}
                    </Typography>
                  </ColumnChart>
                  <ColumnChart lg={10} sm={8} xs={8} padding="0 28px 0 0">
                    <Typography
                      color="black1"
                      variant="body2"
                      fontWeight="regular"
                      fontSize="12px"
                      lineHeight="18px"
                      tabIndex="0"
                    >
                      Órdenes de compras generadas en
                      <br />
                      <strong>{item.name} </strong>
                    </Typography>
                  </ColumnChart>
                </Row>
              ))}
            </ColumnChart>
          </>
        </RowChart>
      )}

      {loading && !error && (
        <>
          <Row flexDirection="column" gap="10px" margin="20px 0 10px 0">
            <Skeleton height="35px" variant="rectangular" width="40%" />
            <Skeleton height="15px" variant="rectangular" width="50%" />
            <Skeleton height="50px" variant="rectangular" width="50%" />
          </Row>
          <Skeleton height="400px" variant="rectangular" width="100%" />
          <Row flexWrap="nowrap" gap="26px" marginTop="32px" justifyContent="center">
            <ColumnChart lg={6} sm={12} xs={12}>
              <Row>
                <Skeleton height="110px" variant="rectangular" width="100%" />
              </Row>
            </ColumnChart>
            <ColumnChart lg={6} sm={12} xs={12} justifyContent="end" margin="0 0 28px 0">
              <Row>
                <Skeleton height="110px" variant="rectangular" width="100%" />
              </Row>
            </ColumnChart>
          </Row>
        </>
      )}
      {!loading && (
        <Row flexWrap="nowrap" gap="26px" marginTop="32px" justifyContent="center">
          <ColumnChart lg={6} sm={12} xs={12} margin={isUpToMedium ? '0 13px 0 0' : '0 0 28px 0'}>
            <RowChart padding="24px 16px 14px 16px">
              <Row>
                <Typography
                  color="black1"
                  fontSize="18px"
                  fontWeight="bold"
                  variant="h4"
                  lineHeight="22px"
                  tabIndex="0"
                >
                  Información sobre montos transados
                </Typography>
              </Row>
              <Row>
                <Typography
                  color="black1"
                  variant="body1"
                  fontWeight="regular"
                  fontSize="16px"
                  lineHeight="21px"
                  tabIndex="0"
                >
                  Si deseas conocer información sobre montos transados por esta institución puedes hacerlo en su ficha
                  en el sitio de Datos Abiertos de ChileCompra.
                </Typography>
              </Row>
              <Row justifyContent="end" alignItems="end">
                <RedirectLink target="_blank" to={`${REACT_APP_OPENED_DATA_URL}ficha-organismo/${entCode}`}>
                  Ir a la ficha en Datos Abiertos <ArrowForwardIcon />
                </RedirectLink>
              </Row>
            </RowChart>
          </ColumnChart>
          <ColumnChart lg={6} sm={12} xs={12} justifyContent="end" margin={isUpToMedium ? '0 0 0 13px' : '0 0 28px 0'}>
            <RowChart padding="24px 16px 14px 16px">
              <Row>
                <Typography
                  color="black1"
                  fontSize="18px"
                  fontWeight="bold"
                  variant="h4"
                  lineHeight="22px"
                  tabIndex="0"
                >
                  Detalle de órdenes de compra
                </Typography>
              </Row>
              <Row>
                <Typography
                  color="black1"
                  variant="body1"
                  fontWeight="regular"
                  fontSize="16px"
                  lineHeight="21px"
                  tabIndex="0"
                >
                  Utiliza el buscador de órdenes de compra de Mercado Público para acceder al listado completo de
                  órdenes generadas por esta institución.
                </Typography>
              </Row>
              <Row justifyContent="end">
                <RedirectLink target="_blank" to={generateSearchPurchaseOrderUrl}>
                  Ir al buscador de órdenes de compra <ArrowForwardIcon />
                </RedirectLink>
              </Row>
            </RowChart>
          </ColumnChart>
        </Row>
      )}
    </Root>
  )
}

export default PurchaseOrdersSection
